<template>
    <div class="image-workbench-generator">
        <div class="generator-interface">
            <div class="workbench-area">
                <ClientOnly>
                    <PcWorkbench
                        lock-expanded
                        :show-inline-login-cta="showInlineLoginCta"
                        model-landing-page
                        :landing-model-key="props.modelKey"
                        default-primary-tab="IMAGE_GENERATE"
                        @ready="handleWorkbenchReady"
                    />

                    <template #fallback>
                        <div class="workbench-fallback" aria-hidden="true">
                            <div class="workbench-fallback__tabs"></div>
                            <div class="workbench-fallback__separator"></div>
                            <div class="workbench-fallback__prompt"></div>
                            <div class="workbench-fallback__actions"></div>
                        </div>
                    </template>
                </ClientOnly>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import PcWorkbench from '~/components/Tool/PcWorkbench.vue'
import { emitter } from '~/utils/emitter'
import { useAppStore } from '~/stores/app'

interface Props {
    modelKey: string
    modelVersion?: string
    defaultTab?: 'image' | 'text'
    showInlineLoginCta?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    modelVersion: '',
    defaultTab: 'image',
    showInlineLoginCta: true,
})

const appStore = useAppStore()
const isWorkbenchReady = ref(false)

const applyWorkbenchPrefill = () => {
    if (!import.meta.client || !isWorkbenchReady.value) return

    const secondaryTab =
        props.defaultTab === 'text' ? 'TEXT_TO_IMAGE' : 'IMAGE_TO_IMAGE'
    const selectedModelKey = props.modelVersion || props.modelKey

    emitter.emit('workbench-apply-prefill', {
        preserveWorkbenchState: true,
        prefill: {
            v: 1,
            ts: Date.now(),
            mainCategorySelected: 'IMAGE_GENERATE',
            specificCategorySelected: secondaryTab,
            selectedModelKey,
        },
    })

    emitter.emit('sync-data-to-workbench', {
        source: 'model-page',
        model: selectedModelKey,
        selectedModelParentKey: props.modelKey,
        model_version: props.modelVersion,
    })
}

const handleWorkbenchReady = () => {
    isWorkbenchReady.value = true
    nextTick(() => {
        applyWorkbenchPrefill()
    })
}

watch(
    () => appStore.modelData,
    modelData => {
        if (!modelData) return
        nextTick(() => {
            applyWorkbenchPrefill()
        })
    },
    { immediate: true }
)

watch(
    () => [props.modelKey, props.modelVersion, props.defaultTab],
    () => {
        nextTick(() => {
            applyWorkbenchPrefill()
        })
    }
)
</script>

<style lang="less" scoped>
.image-workbench-generator {
    width: min(100%, 85vw);
    max-width: 85vw;
    margin: 0 auto;
}

.generator-interface {
    width: 100%;
}

.workbench-area {
    min-width: 0;
}

.workbench-fallback {
    display: flex;
    box-sizing: border-box;
    min-height: 295px;
    flex-direction: column;
    gap: 20px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    padding: 16px;
    background: rgba(28, 28, 30, 0.8);
}

.workbench-fallback__tabs {
    width: 100%;
    height: 36px;
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.05);
}

.workbench-fallback__separator {
    width: 100%;
    height: 1px;
    flex: 0 0 1px;
    background: rgba(255, 255, 255, 0.05);
}

.workbench-fallback__prompt {
    width: 100%;
    min-height: 120px;
    flex: 1;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.04);
}

.workbench-fallback__actions {
    width: min(38%, 320px);
    height: 44px;
    align-self: flex-end;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.06);
}

.workbench-area :deep(.workbench-container.pc-workbench) {
    width: 100%;
    max-width: none;
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(28, 28, 30, 0.8);
    backdrop-filter: blur(50px);
}

@media (max-width: 1024px) {
    .image-workbench-generator,
    .generator-interface {
        width: 100%;
        max-width: 100%;
    }
}
</style>
